// react
import { useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';

import 'highcharts/modules/no-data-to-display';

import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

/**
 * formatTimestamp
 * Returns a Danish “weekday day month” label (e.g. “tir 25 dec”)
 * The input can be a Date, a millisecond timestamp, or an ISO string.
 */
const formatTimestamp = (ts) =>
  new Date(ts).toLocaleDateString('da-DK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const PowerLoadProfilesAnalysisChart = ({
  loadData,
  isLoading = false,
  powerData,
}) => {
  const chartHeight = useResponsiveChartHeight();
  const { effect } = useEnergyGroupUnit();
  const chartRef = useRef(null);
  const { palette } = useTheme();
  const themeOptions = palette.mode === 'dark' ? darkTheme : lightTheme;
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  /* -------- loading indicator -------- */
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) {
      chart.showLoading('Indlæser data…');
      chart.hideNoData();
    } else {
      chart.hideLoading();
    }
  }, [isLoading]);

  /* -------- series + categories -------- */
  /* ---------- series + x-axis values (differential) ---------- */
  const { chartSeries, xMin, xMax } = useMemo(() => {
    if (!loadData?.length) return { chartSeries: [] };
    if (!powerData?.length) return { chartSeries: [] };

    const toMs = (ts) => new Date(ts).getTime();

    // Get the min/max range from loadData
    const loadMin = toMs(loadData[0].timestamp);
    const loadMax = toMs(loadData[loadData.length - 1].timestamp);

    // Filter powerData to match loadData range
    const filteredPowerData = powerData.filter((d) => {
      const ts = toMs(d.timestamp);
      return ts >= loadMin && ts <= loadMax;
    });

    const categories = filteredPowerData.map((d) => toMs(d.timestamp));

    // helper to map an array of point values into [x, y] pairs
    const pair = (values) => values.map((y) => [toMs(y.timestamp), y.value]);

    return {
      chartSeries: [
        {
          name: t('power_load_profiles.consumption'),
          type: 'area',
          data: pair(filteredPowerData),
          color: 'hsla(0, 2.00%, 48.40%, 0.45)',
          tooltip: { valueDecimals: 1 },
          states: {
            inactive: {
              opacity: 1, // disables dimming
            },
            hover: {
              halo: { size: 0 }, // optional: removes glow effect
            },
          },
        },
        {
          name: t('power_load_profiles.peak'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.peak_load,
            })),
          ),
          color: 'hsla(0, 75%, 60%, 0.2)',
          tooltip: { valueDecimals: 1 },
          states: {
            inactive: {
              opacity: 1, // disables dimming
            },
            hover: {
              halo: { size: 0 }, // optional: removes glow effect
            },
          },
        },
        {
          name: t('power_load_profiles.intermediate'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.intermediate_load,
            })),
          ),
          color: 'rgba(244, 192, 37, 0.35)',
          tooltip: { valueDecimals: 1 },
          states: {
            inactive: {
              opacity: 1, // disables dimming
            },
            hover: {
              halo: { size: 0 }, // optional: removes glow effect
            },
          },
        },

        {
          name: t('power_load_profiles.base'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.base_load,
            })),
          ),
          color: 'hsla(120, 60%, 45%, 0.5)',
          tooltip: { valueDecimals: 1 },
          states: {
            inactive: {
              opacity: 1, // disables dimming
            },
            hover: {
              halo: { size: 0 }, // optional: removes glow effect
            },
          },
        },
      ],
      xMin: categories.at(0),
      xMax: categories.at(-1),
    };
  }, [loadData, powerData, t]);

  /* -------- options -------- */

  const options = useMemo(
    () => ({
      lang: {
        noData: 'Ingen data',
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      chart: {
        type: 'area',
        zooming: {
          type: 'x',
        },
        spacingLeft: 40,
        spacingRight: 40,
        height: chartHeight,
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          load() {
            const chart = this;
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              // Tooltip
              chart.tooltip?.hide(0);

              // Clear hovered points (shared tooltip can use hoverPoints)
              if (chart.hoverPoints?.length) {
                chart.hoverPoints.forEach((p) => p.setState(''));
                chart.hoverPoints.length = 0;
              }

              // Clear single hovered point
              if (chart.hoverPoint) {
                chart.hoverPoint.setState('');
                chart.hoverPoint = null;
              }

              // Clear hovered series (removes “glow/thickening” highlight)
              if (chart.hoverSeries) {
                chart.hoverSeries.setState('');
                chart.hoverSeries = null;
              }

              // Clear series/point states (also removes inactive dimming)
              chart.series.forEach((s) => {
                s.setState('');
                // Points can keep state in some cases (markers/halos)
                if (s.points) s.points.forEach((p) => p.setState(''));
              });

              // Reset pointer + crosshairs
              chart.pointer?.reset?.(true);
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
      },
      time: { useUTC: false },
      credits: { enabled: false },
      title: { text: '' },
      xAxis: {
        type: 'datetime',
        min: xMin,
        max: xMax,
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        labels: {
          formatter() {
            return formatTimestamp(this.value);
          },
          style: { color: themeOptions.xAxis.labels.style.color },
        },
        lineColor: themeOptions.xAxis.lineColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: `Last (${effect})`,
          style: { color: themeOptions.yAxis.title.style.color },
        },
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
        stackLabels: {
          enabled: true,
          formatter() {
            return formatNumber(this.total);
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: palette.mode === 'dark' ? 'white' : 'black',
        },
        formatter() {
          return (
            this.points
              .map(
                (p) =>
                  `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:5px;"></span>` +
                  `${p.series.name}: <b>${formatNumber(p.y)} ${effect}</b>`,
              )
              .join('<br/>') +
            `<br/><span style="font-size:12px;color:gray;">${formatTimestamp(
              this.x,
            )}</span>`
          );
        },
      },
      legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },

      series: chartSeries,
    }),
    [
      chartHeight,
      themeOptions.chart.backgroundColor,
      themeOptions.xAxis.labels.style.color,
      themeOptions.xAxis.lineColor,
      themeOptions.yAxis.title.style.color,
      themeOptions.yAxis.labels.style.color,
      themeOptions.tooltip.backgroundColor,
      themeOptions.tooltip.borderColor,
      themeOptions.legend.itemStyle.color,
      xMin,
      xMax,
      effect,
      palette.mode,
      chartSeries,
    ],
  );

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

export default PowerLoadProfilesAnalysisChart;
